import type {
  APITaskArtifact,
  ArtifactState,
  BranchArtifact,
  BranchArtifactData,
  GitHubArtifactData,
  GitHubResourceArtifact,
} from '../types/artifact'
import type {HydratedAPITask} from '../types/api-task'
import type {Pull} from '../types/pull'
import type {PullRequestStatus} from '@github-ui/agent-sessions-interface/components/PullRequestStatusIcon'

/**
 * Type guard to check if an artifact is a branch artifact.
 */
function isBranchArtifact(artifact: APITaskArtifact): artifact is BranchArtifact {
  return artifact.type === 'branch' && artifact.provider === 'github'
}

/**
 * Type guard to check if an artifact is a GitHub resource artifact.
 */
function isGitHubResourceArtifact(artifact: APITaskArtifact): artifact is GitHubResourceArtifact {
  return artifact.type === 'github_resource' && artifact.provider === 'github'
}

export const getBranchArtifactData = (artifacts?: APITaskArtifact[]): BranchArtifactData | undefined => {
  const branchArtifact = artifacts?.find(isBranchArtifact)
  return branchArtifact?.data
}

/**
 * Resolves the branch name to use for "Continue in…" affordances, preferring an
 * explicit branch name, then the task's branch artifact, then the pull request's
 * head ref. Returns '' when none are available.
 */
export function resolveEffectiveBranchName(
  branchName: string | undefined,
  task: Pick<HydratedAPITask, 'artifacts'> | undefined,
  pull: Pick<Pull, 'head_ref_name'> | undefined,
): string {
  return branchName || getBranchArtifactData(task?.artifacts)?.head_ref || pull?.head_ref_name || ''
}

/**
 * Checks if the base_ref and head_ref are the same.
 */
export function baseRefMatchesHeadRef(data?: BranchArtifactData): boolean {
  return data?.base_ref !== undefined && data.base_ref === data.head_ref
}

export const getGitHubArtifactData = (artifacts?: APITaskArtifact[]): GitHubArtifactData | undefined => {
  const ghArtifact = artifacts?.find(isGitHubResourceArtifact)
  return ghArtifact?.data
}

const MAX_PULL_REQUEST_ARTIFACTS = 3

/** Finds every distinct pull-request `github_resource` artifact on the task, deduped by id and capped at {@link MAX_PULL_REQUEST_ARTIFACTS}. */
export function getGitHubArtifactsData(artifacts?: APITaskArtifact[]): GitHubArtifactData[] {
  if (!artifacts) return []
  const seenIds = new Set<number>()
  const results: GitHubArtifactData[] = []
  for (const artifact of artifacts) {
    // The backend serializes a pull request's resource type as `"pull"`, not `"pull_request"`.
    if (!isGitHubResourceArtifact(artifact) || artifact.data.type !== 'pull') continue
    if (seenIds.has(artifact.data.id)) continue
    seenIds.add(artifact.data.id)
    results.push(artifact.data)
    if (results.length >= MAX_PULL_REQUEST_ARTIFACTS) break
  }
  return results
}

export function getEffectivePrState(
  artifactState: ArtifactState | undefined,
  pull: Pick<Pull, 'state' | 'reviewable_state'> | undefined,
): ArtifactState | undefined {
  // Prefer the hydrated pull object: its `reviewable_state` is the authoritative
  // draft-vs-ready signal. The artifact snapshot's collapsed `state` enum is
  // unreliable for draft (it can report `'open'` for a PR that is actually a
  // draft), so it is only used as a fallback when no pull has been hydrated.
  if (pull) {
    return pull.state === 'open' && pull.reviewable_state === 'draft' ? 'draft' : pull.state
  }
  return artifactState
}

/** Maps a pull request's lifecycle to the richer `PullRequestStatus` vocabulary (adds `merge-queue`) used by the compact status icon/button. */
export function getPullRequestStatus(
  pull: Pick<Pull, 'state' | 'reviewable_state' | 'inMergeQueue'>,
): PullRequestStatus {
  const effectiveState = getEffectivePrState(undefined, pull)
  if (effectiveState === 'merged') return 'merged'
  if (effectiveState === 'closed') return 'closed'
  if (pull.inMergeQueue) return 'merge-queue'
  return effectiveState === 'draft' ? 'draft' : 'open'
}
